import { useState } from 'react'
import { OnScreenModal } from '@/components/ui/OnScreenModal'
import { YardiTCodeMappingControls } from '@/features/resident/components/detail/YardiTCodeMappingControls'
import { updatePmsResidentMatchStatus } from '@/features/resident/services/yardiResidentService'
import { showApiError, showSuccess } from '@/lib/utils/notifications'
import modalStyles from '@/components/ui/claimsModals.module.css'
import { cn } from '@/lib/utils/helpers'
import type { MatchingResidentRow } from '../types'

const REASONS = [
  { id: 'HISTORICAL', label: 'Historical' },
  { id: 'NO_PMS_EQUIVALENT', label: 'No PMS equivalent' },
  { id: 'INSUFFICIENT_DATA', label: 'Insufficient data' },
  { id: 'INVALID_PMS_RECORD', label: 'Invalid PMS record' },
  { id: 'DUPLICATE_PMS_RECORD', label: 'Duplicate PMS record' },
  { id: 'BUSINESS_DECISION', label: 'Business decision not to map' },
]

const NOTE_MAX = 500

const FIELD =
  'mt-1.5 h-10 w-full rounded-lg border border-slate-200 bg-white px-3 text-sm text-slate-900 shadow-sm focus:border-[rgb(21_151_255)] focus:outline-none focus:ring-2 focus:ring-[rgb(21_151_255)]/20'

const TEXTAREA =
  'mt-1.5 min-h-[96px] w-full rounded-lg border border-slate-200 bg-white px-3 py-2 text-sm text-slate-900 shadow-sm focus:border-[rgb(21_151_255)] focus:outline-none focus:ring-2 focus:ring-[rgb(21_151_255)]/20'

function residentName(row: MatchingResidentRow | null): string {
  if (!row) return ''
  return `${row.first_name || ''} ${row.last_name || ''}`.trim() || `Resident ${row.liberty_entity_id}`
}

export function UnmatchableDialog({
  row,
  onClose,
  onSaved,
}: {
  row: MatchingResidentRow | null
  onClose: () => void
  onSaved: () => void
}) {
  const [action, setAction] = useState<'status' | 'map'>('status')
  const [reason, setReason] = useState(row?.unmatchable_reason || 'HISTORICAL')
  const [note, setNote] = useState(row?.admin_note || '')
  const [saving, setSaving] = useState(false)

  const save = async (status: 'UNMATCHABLE' | 'PENDING') => {
    if (!row) return
    setSaving(true)
    try {
      await updatePmsResidentMatchStatus({
        property_id: row.liberty_property_id,
        company_id: row.liberty_company_id,
        property_code: row.source_property_id,
        resident_id: row.liberty_entity_id,
        resident_default_id: row.liberty_entity_id,
        status,
        reason: status === 'UNMATCHABLE' ? reason : undefined,
        note: note.trim() || undefined,
      })
      showSuccess(
        status === 'PENDING'
          ? 'Resident reset to pending.'
          : 'Resident marked as permanently skipped and will be excluded from the next sync.',
      )
      onSaved()
      onClose()
    } catch (error) {
      showApiError(error)
    } finally {
      setSaving(false)
    }
  }

  return (
    <OnScreenModal
      isOpen={!!row}
      title="Mark Permanent Skipped"
      onClose={onClose}
      maxWidth="max-w-2xl"
      bodyScrollOnly
      bodyClassName="p-4 sm:p-5"
      overlayDataTestId="yardi-matching-status-dialog-overlay"
      panelDataTestId="yardi-matching-status-dialog"
      closeButtonDataTestId="yardi-matching-status-dialog-close-button"
      footer={
        <div className={modalStyles.modalFooterActionGroup} data-testid="yardi-matching-status-dialog-footer">
          {action === 'status' ? (
            <button
              type="button"
              className={modalStyles.modalActionPrimary}
              disabled={saving}
              onClick={() => void save('UNMATCHABLE')}
              data-testid="yardi-matching-status-save"
            >
              {saving ? 'Saving…' : 'Mark Permanent Skipped'}
            </button>
          ) : null}
          {action === 'status' ? (
            <button
              type="button"
              className={modalStyles.modalActionSecondary}
              disabled={saving}
              onClick={() => void save('PENDING')}
            >
              Reset to pending
            </button>
          ) : null}
          <button
            type="button"
            className={modalStyles.modalActionSecondary}
            onClick={onClose}
            disabled={saving}
            data-testid="yardi-matching-status-dialog-cancel-button"
          >
            Cancel
          </button>
        </div>
      }
    >
      <div className="space-y-4 text-sm">
        <div className="border-b border-slate-200">
          <nav className="flex gap-6" role="tablist" aria-label="Mark Permanent Skipped">
            {(
              [
                ['status', 'Permanent Skip'],
                ['map', 'Manually Map'],
              ] as const
            ).map(([id, label]) => (
              <button
                key={id}
                type="button"
                role="tab"
                aria-selected={action === id}
                data-testid={`yardi-matching-status-tab-${id}`}
                className={cn(
                  '-mb-px border-b-2 px-0.5 pb-2.5 text-sm font-medium',
                  action === id
                    ? 'border-[rgb(21_151_255)] text-[rgb(21_151_255)]'
                    : 'border-transparent text-slate-500 hover:text-[#232946]',
                )}
                onClick={() => setAction(id)}
              >
                {label}
              </button>
            ))}
          </nav>
        </div>

        <div className="grid grid-cols-1 gap-3 sm:grid-cols-3">
          <div>
            <p className="text-sm font-medium text-[#232946]">Resident Name</p>
            <p className="mt-1 text-sm text-slate-700">{residentName(row)}</p>
          </div>
          <div>
            <p className="text-sm font-medium text-[#232946]">TCode</p>
            <p className="mt-1 text-sm text-slate-700">{row?.matched_tcode || '--'}</p>
          </div>
          <div>
            <p className="text-sm font-medium text-[#232946]">Email</p>
            <p className="mt-1 break-all text-sm text-slate-700">{row?.email || '--'}</p>
          </div>
        </div>

        {action === 'map' && row ? (
          <YardiTCodeMappingControls
            key={row.match_status_id}
            propertyId={row.liberty_property_id}
            residentDefaultId={row.liberty_entity_id}
            companyId={row.liberty_company_id}
            propertyCode={row.source_property_id}
            currentTCode={row.matched_tcode}
            onMapped={() => {
              onSaved()
              onClose()
            }}
            testIdPrefix="yardi-matching-status"
          />
        ) : (
          <div className="grid grid-cols-1 gap-4">
            <label className="text-sm font-medium text-[#232946]">
              Reason
              <select
                value={reason}
                onChange={(event) => setReason(event.target.value)}
                className={FIELD}
                data-testid="yardi-matching-status-reason"
              >
                {REASONS.map((item) => (
                  <option key={item.id} value={item.id}>
                    {item.label}
                  </option>
                ))}
              </select>
            </label>
            <label className="text-sm font-medium text-[#232946]">
              Note <span className="font-normal text-slate-400">(optional)</span>
              <textarea
                value={note}
                maxLength={NOTE_MAX}
                onChange={(event) => setNote(event.target.value.slice(0, NOTE_MAX))}
                placeholder="Add note (optional)..."
                className={TEXTAREA}
                data-testid="yardi-matching-status-note"
              />
              <span className="mt-1 block text-right text-xs text-slate-400">
                {note.length}/{NOTE_MAX}
              </span>
            </label>
          </div>
        )}
      </div>
    </OnScreenModal>
  )
}
